// client/src/hooks/useTemplateMensagem.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api'; 
import useAlert from './useAlert';

const BASE_URL = '/templates/mensagem'; 

// ✅ HOOK PARA LISTAR TEMPLATES DE MENSAGEM
export const useTemplatesMensagem = (filters = {}, options) => {
    const { setAlert } = useAlert();
    const queryClient = useQueryClient();

    const queryKey = ['templatesMensagem', filters]; 

    return useQuery({
        queryKey: queryKey,
        queryFn: async () => {
            const params = new URLSearchParams(filters).toString();
            const url = `${BASE_URL}${params ? `?${params}` : ''}`;
            console.log(`FRONTEND LOG: useTemplatesMensagem - Fetching URL: ${url}`);
            const response = await apiClient.get(url); 
            // ✅ CORREÇÃO ESSENCIAL AQUI: 
            // Sua API provavelmente retorna um objeto `{ success: true, count: X, data: [...] }`.
            // Estamos extraindo o array `data` desse objeto.
            // Se a API retornar o array diretamente, `response.data` já será o array.
            // `response.data.data || []` lida com ambos os cenários e garante um array.
            return response.data.data || response.data || []; 
        },
        onError: (error) => {
            console.error(`Erro ao buscar templates de mensagem:`, error);
            setAlert('Erro ao carregar templates de mensagem.', 'danger');
        },
        ...options,
    });
};

// ✅ HOOK PARA OBTER UM TEMPLATE DE MENSAGEM POR ID
export const useTemplateMensagemById = (id, options) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: ['templateMensagem', id], 
        queryFn: async () => {
            const response = await apiClient.get(`${BASE_URL}/${id}`); 
            // ✅ CORREÇÃO AQUI: Similarmente, extrai o dado se estiver aninhado em 'data'
            return response.data.data || response.data;
        },
        enabled: !!id, 
        onError: (error) => {
            console.error(`Erro ao buscar template de mensagem ${id}:`, error);
            setAlert('Erro ao carregar template de mensagem.', 'danger');
        },
        ...options,
    });
};

// ✅ HOOK PARA MUTAÇÕES (ADICIONAR, ATUALIZAR, DELETAR) TEMPLATES DE MENSAGEM
export const useMutateTemplateMensagem = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const addTemplateMensagem = useMutation({
        mutationFn: async (templateData) => {
            const response = await apiClient.post(BASE_URL, templateData); 
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['templatesMensagem'] }); 
            setAlert('Template de mensagem criado com sucesso!', 'success');
        },
        onError: (error) => {
            console.error('Erro ao criar template de mensagem:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido ao criar template.';
            setAlert(errorMessage, 'danger');
        },
    });

    const updateTemplateMensagem = useMutation({
        mutationFn: async ({ id, templateData }) => {
            const response = await apiClient.put(`${BASE_URL}/${id}`, templateData); 
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['templatesMensagem'] });
            queryClient.invalidateQueries({ queryKey: ['templateMensagem', data._id] }); 
            setAlert('Template de mensagem atualizado com sucesso!', 'success');
        },
        onError: (error) => {
            console.error('Erro ao atualizar template de mensagem:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido ao atualizar template.';
            setAlert(errorMessage, 'danger');
        },
    });

    const deleteTemplateMensagem = useMutation({
        mutationFn: async (id) => {
            await apiClient.delete(`${BASE_URL}/${id}`); 
            return id;
        },
        onSuccess: (id) => {
            queryClient.invalidateQueries({ queryKey: ['templatesMensagem'] }); 
            setAlert('Template de mensagem excluído com sucesso!', 'success');
        },
        onError: (error) => {
            console.error('Erro ao excluir template de mensagem:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido ao excluir template.';
            setAlert(errorMessage, 'danger');
        },
    });

    return {
        addTemplateMensagem,
        updateTemplateMensagem,
        deleteTemplateMensagem,
        isPendingMensagem: addTemplateMensagem.isPending || updateTemplateMensagem.isPending || deleteTemplateMensagem.isPending,
    };
};